import {makeAutoObservable, runInAction} from "mobx";
import {downloadCertificate} from "requests";
import {logEvent} from "analytics";
import {reprNetworkError} from "utils";
import {gtmCertificate, type CertificateSurface} from "utils/gtm";
import {hasCertificate, type CertificateSource} from "utils/certificate";
import type {RootStore} from "stores/rootStore";
import type {ScanMeasurement} from 'utils/funnel';

export class CertificateStore {
    root: RootStore;
    /** A stored report's analysis, once the server says its author's plan gives it a
     *  certificate (ReportPage); unset, the report page shows no download at all. */
    reportUuid: string | null = null;
    isDownloading: boolean = false;
    private scanMeasurements = new Map<string, ScanMeasurement>();

    /** Bind a certificate to its own scan, even after another tab has run.
     * History loaded from an earlier session has no client attempt to attach. */
    rememberAnalysis(measurement: ScanMeasurement) {
        if (!measurement.analysis_uuid) return;
        this.scanMeasurements.delete(measurement.analysis_uuid);
        this.scanMeasurements.set(measurement.analysis_uuid, {...measurement});
        if (this.scanMeasurements.size > 50) {
            this.scanMeasurements.delete(this.scanMeasurements.keys().next().value!);
        }
    }

    constructor(root: RootStore) {
        this.root = root;
        makeAutoObservable(this);
    }

    setReportUuid(uuid: string | null) {
        this.reportUuid = uuid;
    }

    /**
     * What the open tab would download: a stored report, the selected batch file, or the
     * tab's own text run. Each tab keeps its own result, so the AI tab's certificate is the
     * analysis of its verdict even after a plagiarism check ran on the same text — never
     * the latest run's.
     */
    private get source(): CertificateSource {
        const {batchFile, input, aiScan, plagiarism, reportViewMode} = this.root;
        if (reportViewMode) {
            return {
                analysisUuid: this.reportUuid,
                aiScore: aiScan.score,
                plagiarismScore: plagiarism.plagiarismData?.score,
                pending: aiScan.isLoading || plagiarism.isLoading,
            };
        }
        if (batchFile.isBatchMode) {
            return batchFile.selectedFile ? batchFile.certificateSource(batchFile.selectedFile) : {};
        }
        return input.activeStore.certificateSource;
    }

    get analysisUuid(): string | null {
        return this.source.analysisUuid ?? null;
    }

    get isAvailable() {
        return hasCertificate(this.source);
    }

    private getScanType(): 'deep_scan' | 'simple_scan' | 'plagiarism' {
        const {input, aiScan} = this.root;
        // The result's own mode; the live flags only where none is recorded (a batch file).
        if (aiScan.scanMode) return aiScan.scanMode === 'deep' ? 'deep_scan' : 'simple_scan';
        if (input.deepScanFlag) return 'deep_scan';
        if (input.aiScanFlag) return 'simple_scan';
        return 'plagiarism';
    }

    /**
     * The sign-in wall in front of every certificate download the VIEWER owns — the scan
     * panel, the batch (per file and "get all"), the history rows. Every account has
     * certificates; an anon is asked to sign in first, unless their own plan carries them
     * (a guest checkout, the standalone tools).
     *
     * A report opened by its link is let through too: the page has already asked the
     * server (`certificate_available`) and only shows a control when the answer was yes.
     *
     * The login hands the download back once it is done, so the user never has to find the
     * button again. An anon's resume has to survive an OAuth login, which reloads the page —
     * hence `analysisUuid`, handed to the login as a persisted action and replayed by
     * LoginCompleteHandler. The batch's "get all" has no single uuid to persist, so it falls
     * back to the in-memory retry, which travels as far as an in-place email login.
     *
     * Returns false when it put up a wall — the caller must not download.
     */
    requireAccess(surface: CertificateSurface, analysisUuid?: string | null, retry?: () => void): boolean {
        const {user, input, reportViewMode} = this.root;
        // An account always gets the server's answer: the wall is a sign-in, which it already did.
        if (reportViewMode || user.canGenerateReport || !user.resultsGated) return true;

        void logEvent(gtmCertificate('wall', surface, {wall: 'login'}));
        // One resume per prompt: the persisted one covers an in-place login as well, so
        // handing the login both of them would download the certificate twice.
        const continuation = analysisUuid
            ? {action: {type: 'download_certificate' as const, uuid: analysisUuid, surface}}
            : {resume: retry};
        input.promptLogin('general', {
            ...continuation,
            analyticsContext: {
                ...(analysisUuid ? this.scanMeasurements.get(analysisUuid) : {}),
                signup_entrypoint: 'download_certificate',
                paywall_trigger: 'download_certificate',
            },
        });
        return false;
    }

    /** `uuid` names the analysis outright when the stores can't — a download resumed after
     *  an OAuth login reloaded the page (LoginCompleteHandler). */
    async download(uuid: string | null = this.analysisUuid,
                   surface: CertificateSurface = this.root.reportViewMode ? 'report_page' : 'scan_panel') {
        const retry = () => { void this.download(uuid, surface); };
        if (!this.requireAccess(surface, uuid, retry)) return;

        if (!uuid) {
            console.error('Cannot download certificate: analysis UUID is not available');
            return;
        }

        this.isDownloading = true;
        const saved = await this.save(surface, uuid);
        runInAction(() => { this.isDownloading = false; });
        // Only the panel's own result says what kind of scan it was — not a row's, nor one
        // resumed after an OAuth reload emptied the panel.
        const ownResult = (surface === 'scan_panel' || surface === 'report_page') && uuid === this.analysisUuid;
        const scanType = ownResult ? this.getScanType() : undefined;
        if (saved) void logEvent(gtmCertificate('download', surface, {scan_type: scanType}));
    }

    /** Fetches the PDF into the browser's downloads. Resolves whether it got there; a failure
     *  is told in a toast. */
    async save(surface: CertificateSurface, uuid: string): Promise<boolean> {
        try {
            await downloadCertificate(uuid);
            return true;
        } catch (e) {
            void logEvent(gtmCertificate('download_error', surface, {error_code: reprNetworkError(e).code}));
            this.root.toast.error('certificate-download-error', e);
        }
        return false;
    }

    clear() {
        this.reportUuid = null;
        this.isDownloading = false;
    }
}
